// src/utils/printTribunalReportPdf.js
import html2pdf from "html2pdf.js";

function escapeHtml(s) {
  return String(s ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function fmtDateDMY(iso) {
  if (!iso) return "—";
  const s = String(iso).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return "—";
  const [y, m, d] = s.split("-");
  return `${d}/${m}/${y}`;
}

function mapRowsForPdf(items) {
  return (items || []).map((exp) => {
    const actor =
      exp?.nombre_trabajador || exp?.nombre_parte_actora || exp?.actor || "—";

    const razones = Array.isArray(exp?.razones_sociales)
      ? exp.razones_sociales.map((r) => r?.razon_social || "").filter(Boolean).join(" / ")
      : "";
    const contra = razones || exp?.empresa_nombre || exp?.contraparte || "—";

    const hasCitatorio =
      exp?.id_tribunal_documento_citatorio !== null &&
      exp?.id_tribunal_documento_citatorio !== undefined &&
      Number(exp?.id_tribunal_documento_citatorio) !== 0;

    const estatus = hasCitatorio ? "Con demanda" : "Sin demanda";

    return {
      expediente: exp?.expediente_format || exp?.expediente || String(exp?.id || "—"),
      partes: `${actor} vs ${contra}`,
      tribunal: exp?.nombre_autoridad || exp?.tribunal || "—",
      estatus,
      proxima_fecha:
        fmtDateDMY(exp?.fecha_audiencia_conciliadora) ||
        fmtDateDMY(exp?.fecha_limite_contestacion) ||
        "—",
    };
  });
}

function buildTribunalReportHtml({
  periodFrom,
  periodTo,
  createdAt,
  rows,
  ciudadesLabel,
  nombreBusqueda,
  autoPrint = false,
}) {
  // Prefijado bajo .oya-pdf-export para que, si html2canvas inserta este
  // bloque temporalmente en el documento real al generar el PDF, sus reglas
  // (body, table, h1, tr...) nunca choquen con los estilos de la app en vivo.
  const ROOT = ".oya-pdf-export";
  const css = `
    ${ROOT} {
      --bg: #eef1f5; --paper: #f3f5f8; --ink: #111827;
      --muted: #6b7280; --line: #d6dae1; --head: #eceff4; --rowAlt: #f7f8fb;
      display: block;
      margin: 0;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif;
      color: var(--ink);
      background: var(--bg);
    }
    ${ROOT} * { box-sizing: border-box; }
    ${ROOT} .page { width: 210mm; min-height: 297mm; margin: 0 auto; background: #fff; padding: 18mm 18mm 24mm 18mm; position: relative; }
    ${ROOT} .letterhead { display: grid; grid-template-columns: 120px 1fr 120px; align-items: center; gap: 12px; margin-bottom: 6px; }
    ${ROOT} .lh-spacer { width: 120px; height: 60px; }
    ${ROOT} .lh-title { text-align: center; line-height: 1.15; }
    ${ROOT} .lh-name { font-weight: 700; letter-spacing: .7px; font-size: 20px; }
    ${ROOT} .lh-sub { margin-top: 5px; font-weight: 500; font-size: 18px; }
    ${ROOT} .lh-rule { border-top: 2px solid #111; margin: 6px 0; }
    ${ROOT} .lh-rule2 { border-top: 1px solid #111; margin: 6px 0; }
    ${ROOT} .lh-contacts { display: grid; grid-template-columns: 1fr 1fr; gap: 18px; font-size: 11px; line-height: 1.35; color: #111827; }
    ${ROOT} .lh-col a { color: #1d4ed8; text-decoration: underline; }
    ${ROOT} h1 { margin: 10px 0 0 0; font-size: 18px; font-weight: 700; text-align: center; color: #1f2937; }
    ${ROOT} .periodBox { margin: 14px 0 18px 0; border: 1px solid var(--line); background: rgba(255,255,255,.40); padding: 12px 14px; border-radius: 10px; font-size: 13px; }
    ${ROOT} .periodRow { display: flex; gap: 16px; margin-bottom: 5px; flex-wrap: wrap; justify-content: space-between; }
    ${ROOT} .periodRow b { font-weight: 700; }
    ${ROOT} hr.sep { border: 0; border-top: 1px solid var(--line); margin: 14px 0 16px 0; }
    ${ROOT} .sectionTitle { font-size: 13px; font-weight: 700; color: #111827; margin: 0 0 8px 0; }
    ${ROOT} table { width: 100%; border-collapse: collapse; border: 1px solid var(--line); background: rgba(255,255,255,.45); margin-top: 6mm; }
    ${ROOT} thead th { background: var(--head); border-bottom: 1px solid var(--line); font-size: 12px; text-align: left; padding: 10px; font-weight: 700; }
    ${ROOT} tbody td { font-size: 12px; padding: 10px; border-top: 1px solid var(--line); vertical-align: top; }
    ${ROOT} tbody tr:nth-child(even) td { background: var(--rowAlt); }
    ${ROOT} .right { text-align: right; }
    ${ROOT} tr { page-break-inside: avoid; }
    ${ROOT} thead { display: table-header-group; }
    @media print { ${ROOT} { background: #fff; } ${ROOT} .page { margin: 0; width: auto; min-height: auto; } }
  `;

  const tbody = (rows || [])
    .map(
      (r) => `
      <tr>
        <td>${escapeHtml(r.expediente)}</td>
        <td>${escapeHtml(r.partes)}</td>
        <td>${escapeHtml(r.tribunal)}</td>
        <td>${escapeHtml(r.estatus)}</td>
        <td>${escapeHtml(r.proxima_fecha)}</td>
      </tr>`
    )
    .join("");

  return `<!doctype html>
<html lang="es">
<head>
<meta charset="utf-8" />
<title>Reporte Tribunal</title>
<style>${css}</style>
</head>
<body>
  <div class="oya-pdf-export">
  <div class="page">
    <div class="letterhead">
      <div class="lh-spacer"></div>
      <div class="lh-title">
        <div class="lh-name">ONTIVEROS &amp; ASOCIADOS</div>
        <div class="lh-sub">ABOGADOS</div>
      </div>
      <div class="lh-spacer"></div>
    </div>
    <div class="lh-rule"></div>
    <div class="lh-rule2"></div>
    <div class="lh-contacts">
      <div class="lh-col">
        <div>Calle "H" y Ave. Marmoleros #1598 Altos</div>
        <div>Col. Industrial, C.P. 21010, Mexicali, Baja California</div>
        <div>Tel. 686 554 54 82</div>
        <div>Correo: <a href="mailto:recepcionmxl@ontiverosyasociados.com.mx">recepcionmxl@ontiverosyasociados.com.mx</a></div>
      </div>
      <div class="lh-col">
        <div>Calle 2 #200 Local 8, Plaza Gabriela, Comercial 83449</div>
        <div>San Luis Río Colorado, Sonora</div>
        <div>Tel. 653 534 14 02, 534 41 91</div>
        <div>Correo: <a href="mailto:victor@ontiverosyasociados.com.mx">victor@ontiverosyasociados.com.mx</a></div>
      </div>
    </div>
    <div class="lh-rule"></div>

    <h1>Materia Laboral - Tribunal</h1>

    <div class="periodBox">
      <div class="periodRow">
        <div><b>Periodo:</b> ${escapeHtml(periodFrom)} - ${escapeHtml(periodTo)}</div>
        <div><b>Fecha de elaboración:</b> ${escapeHtml(createdAt)}</div>
      </div>
      <div class="periodRow">
        <div><b>Búsqueda:</b> ${escapeHtml(nombreBusqueda)}</div>
        <div><b>Ciudades:</b> ${escapeHtml(ciudadesLabel || "—")}</div>
      </div>
    </div>

    <hr class="sep" />

    <div class="sectionTitle">Listado de expedientes (${rows.length})</div>
    <table>
      <thead>
        <tr>
          <th style="width:16%">Expediente</th>
          <th>Actor vs Empresa</th>
          <th style="width:20%">Tribunal</th>
          <th style="width:12%">Estatus</th>
          <th style="width:14%">Próxima fecha</th>
        </tr>
      </thead>
      <tbody>${tbody}</tbody>
    </table>
  </div>
  </div>
  <script>
    window.__AUTO_PRINT__ = ${autoPrint ? "true" : "false"};
    window.onload = () => { if (window.__AUTO_PRINT__) setTimeout(() => window.print(), 50); };
  </script>
</body>
</html>`;
}

export function previewTribunalReportPdf({ items, periodFrom, periodTo, ciudadesLabel, nombreBusqueda }) {
  const rows = mapRowsForPdf(items || []);
  const now = new Date();
  const createdAt = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  return buildTribunalReportHtml({ periodFrom, periodTo, createdAt, rows, ciudadesLabel, nombreBusqueda, autoPrint: false });
}

export function printTribunalReportPdf({ items, periodFrom, periodTo, ciudadesLabel, nombreBusqueda }) {
  const rows = mapRowsForPdf(items || []);
  const now = new Date();
  const createdAt = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  const html = buildTribunalReportHtml({ periodFrom, periodTo, createdAt, rows, ciudadesLabel, nombreBusqueda, autoPrint: true });

  downloadHtmlAsPdf(html, "reporte_tribunal.pdf");
}

// Se renderiza fuera de la vista (fixed + opacity 0) para que html2canvas
// pueda medir el nodo sin provocar ningún parpadeo/zoom en la página real.
function downloadHtmlAsPdf(html, filename) {
  const container = document.createElement("div");
  container.style.position = "fixed";
  container.style.top = "0";
  container.style.left = "0";
  container.style.width = "210mm";
  container.style.zIndex = "-1";
  container.style.opacity = "0";
  container.style.pointerEvents = "none";
  container.innerHTML = html;
  document.body.appendChild(container);

  const node = container.querySelector(".oya-pdf-export") || container;
  const cleanup = () => { if (container.parentNode) container.parentNode.removeChild(container); };

  html2pdf()
    .set({ margin: 0, filename, html2canvas: { scale: 2 }, jsPDF: { unit: "mm", format: "a4", orientation: "portrait" } })
    .from(node)
    .save()
    .then(cleanup)
    .catch(cleanup);
}
